import { useRef, useState } from 'react';
import { Upload, Trash2, AlertTriangle } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useSettingsStore from '../../store/settingsStore';
import { dialog } from '../../store/dialogStore';

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const { updateMe, changePassword, uploadAvatar, deleteAvatar, resetOrg } = useSettingsStore();
  const fileRef = useRef(null);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetPwd, setResetPwd] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [rmsg, setRmsg] = useState(null);

  const [name, setName] = useState(user?.name || '');
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [nw2, setNw2] = useState('');
  const [pmsg, setPmsg] = useState(null);

  const saveProfile = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await updateMe({ name: name.trim() });
      await fetchMe();
      setMsg({ type: 'ok', text: 'Perfil atualizado.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    }
  };

  const pickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { setMsg({ type: 'err', text: 'Envie um arquivo de imagem.' }); return; }
    if (file.size > 5 * 1024 * 1024) { setMsg({ type: 'err', text: 'A imagem deve ter até 5MB.' }); return; }
    setUploading(true);
    setMsg(null);
    try {
      await uploadAvatar(file);
      await fetchMe();
      setMsg({ type: 'ok', text: 'Foto atualizada.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!user?.avatar_url) return;
    if (!(await dialog.confirm('Remover foto', 'Remover foto de perfil?', { confirmLabel: 'Remover', danger: true }))) return;
    setMsg(null);
    try {
      await deleteAvatar();
      await fetchMe();
      setMsg({ type: 'ok', text: 'Foto removida.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    }
  };

  const initials = (user?.name || '?')
    .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  const doReset = async (e) => {
    e.preventDefault();
    setRmsg(null);
    if (!resetConfirm) { setRmsg({ type: 'err', text: 'Confirme que entende que é irreversível.' }); return; }
    setResetLoading(true);
    try {
      await resetOrg(resetPwd);
      window.location.href = '/';
    } catch (err) {
      setRmsg({ type: 'err', text: err.message });
      setResetLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPmsg(null);
    if (nw !== nw2) { setPmsg({ type: 'err', text: 'As senhas não conferem.' }); return; }
    try {
      await changePassword(cur, nw);
      setCur(''); setNw(''); setNw2('');
      setPmsg({ type: 'ok', text: 'Senha atualizada.' });
    } catch (err) {
      setPmsg({ type: 'err', text: err.message });
    }
  };

  return (
    <div className="p-8 max-w-2xl">
      <h2 className="text-2xl font-bold text-text-primary mb-6">Meu perfil</h2>

      <form onSubmit={saveProfile} className="bg-card border border-border rounded-lg p-5 space-y-4 mb-6">
        <h3 className="text-sm font-semibold text-text-primary">Dados pessoais</h3>
        <div>
          <label className="block text-xs text-text-secondary mb-1">E-mail</label>
          <input
            disabled value={user?.email || ''}
            className="w-full bg-main/50 border border-border rounded-lg px-3 py-2 text-sm text-text-secondary"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Nome</label>
          <input
            required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-2">Foto de perfil</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-accent/20 text-accent flex items-center justify-center text-2xl font-semibold overflow-hidden shrink-0">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={pickFile}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-accent/50 text-text-primary transition-colors disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Enviando...' : (user?.avatar_url ? 'Trocar foto' : 'Enviar foto')}
              </button>
              {user?.avatar_url && (
                <button
                  type="button"
                  onClick={removeAvatar}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-danger/50 text-text-secondary hover:text-danger transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remover
                </button>
              )}
              <p className="text-[11px] text-text-secondary/70">JPG, PNG ou GIF. Máximo 5MB.</p>
            </div>
          </div>
        </div>
        {msg && (
          <div className={`text-xs ${msg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{msg.text}</div>
        )}
        <div className="flex justify-end">
          <button className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/80 transition-colors">
            Salvar
          </button>
        </div>
      </form>

      <form onSubmit={savePassword} className="bg-card border border-border rounded-lg p-5 space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">Alterar senha</h3>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Senha atual</label>
          <input
            required type="password" value={cur} onChange={(e) => setCur(e.target.value)}
            className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Nova senha (mín. 6)</label>
          <input
            required minLength={6} type="password" value={nw} onChange={(e) => setNw(e.target.value)}
            className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Confirmar nova senha</label>
          <input
            required type="password" value={nw2} onChange={(e) => setNw2(e.target.value)}
            className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
          />
        </div>
        {pmsg && (
          <div className={`text-xs ${pmsg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{pmsg.text}</div>
        )}
        <div className="flex justify-end">
          <button className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/80 transition-colors">
            Alterar senha
          </button>
        </div>
      </form>

      <div className="bg-card border border-danger/40 rounded-lg p-5 mt-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-danger">Começar do zero</h3>
            <p className="text-xs text-text-secondary mt-1">
              Apaga todos os boards, workspaces, cards, convidados e papéis desta organização.
              Sua conta de administrador é mantida. Esta ação é irreversível.
            </p>
            {!resetOpen ? (
              <button
                onClick={() => setResetOpen(true)}
                className="mt-3 px-3 py-1.5 text-xs border border-danger/50 text-danger rounded-lg hover:bg-danger/10 transition-colors"
              >
                Começar do zero
              </button>
            ) : (
              <form onSubmit={doReset} className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Confirme com sua senha</label>
                  <input
                    required type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)}
                    className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-danger/50"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-text-secondary cursor-pointer">
                  <input
                    type="checkbox" checked={resetConfirm} onChange={(e) => setResetConfirm(e.target.checked)}
                    className="mt-0.5"
                  />
                  Entendo que todos os dados da organização serão apagados e a ação é irreversível.
                </label>
                {rmsg && (
                  <div className={`text-xs ${rmsg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{rmsg.text}</div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setResetOpen(false); setResetPwd(''); setResetConfirm(false); setRmsg(null); }}
                    className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="px-3 py-1.5 text-xs bg-danger text-white rounded-lg hover:bg-danger/80 transition-colors disabled:opacity-50"
                  >
                    {resetLoading ? 'Apagando...' : 'Apagar e recomeçar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
